import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { fetchContent } from "@/lib/api";
import {
  SERVICES, HOME_FAQS, FAQ_PAGE_GROUPS, BLOG_POSTS, BLOG_CATEGORIES,
  RESOURCES, RESOURCE_CATEGORIES,
} from "@/data/content";

const STATIC_DEFAULTS = {
  services: SERVICES,
  homeFaqs: HOME_FAQS,
  faqGroups: FAQ_PAGE_GROUPS,
  posts: BLOG_POSTS,
  blogCategories: BLOG_CATEGORIES,
  resources: RESOURCES,
  resourceCategories: RESOURCE_CATEGORIES,
  testimonials: [],
  settings: null,
  seo: null,
};

const GROUPS = [
  ["general", "General"],
  ["services", "Services & Approach"],
  ["booking", "Booking & Logistics"],
];

const ContentContext = createContext(STATIC_DEFAULTS);

export function ContentProvider({ children }) {
  const [api, setApi] = useState(null);

  useEffect(() => {
    fetchContent().then(setApi).catch(() => {});
  }, []);

  const value = useMemo(() => {
    if (!api) return STATIC_DEFAULTS;
    const services = api.services?.length ? api.services : SERVICES;
    const posts = api.posts ?? BLOG_POSTS;
    const faqs = api.faqs ?? [];
    const homeFaqs = faqs.filter((f) => f.group === "home");
    const faqGroups = GROUPS.map(([key, title]) => ({
      title,
      items: faqs.filter((f) => f.group === key),
    })).filter((g) => g.items.length > 0);
    const resources = api.resources ?? RESOURCES;
    const resourceCategories = [...new Set(resources.map((r) => r.category).filter(Boolean))];
    const blogCategories = [...new Set([...BLOG_CATEGORIES, ...posts.map((p) => p.category)])];
    return {
      services,
      posts,
      homeFaqs: homeFaqs.length ? homeFaqs : HOME_FAQS,
      faqGroups: faqGroups.length ? faqGroups : FAQ_PAGE_GROUPS,
      blogCategories,
      resources,
      resourceCategories: resourceCategories.length ? resourceCategories : RESOURCE_CATEGORIES,
      testimonials: api.testimonials ?? [],
      settings: api.settings || null,
      seo: api.seo || null,
    };
  }, [api]);

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export const useContent = () => useContext(ContentContext);
